// ---------------------------------------------------------------------------
// SpilloverForm — RHF + Zod + TanStack Query + Sonner.
//
// Lets an operator configure the per-campaign spillover policy:
//   * mode = "cancel" — reject over-cap conversions (strict cap).
//   * mode = "allow"  — accept over-cap conversions (soft cap).
//
// Backed by the API contract in `apps/api/src/routes/campaign-spillover.ts`.
// ---------------------------------------------------------------------------

import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useId } from "react";
import { useForm, Controller } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import {
	CAP_TYPES,
	getSpillover,
	saveSpillover,
	SPILLOVER_MODES,
	SpilloverApiError,
	type SpilloverView,
} from "./api";

const schema = z.object({
	campaignId: z
		.string()
		.min(1, "Campaign ID is required")
		.max(128, "Campaign ID is too long"),
	mode: z.enum(SPILLOVER_MODES),
	capType: z.enum(CAP_TYPES),
});

export type SpilloverFormValues = z.infer<typeof schema>;

const defaultValues: SpilloverFormValues = {
	campaignId: "",
	mode: "cancel",
	capType: "conversions",
};

export function SpilloverForm() {
	const formId = useId();
	const queryClient = useQueryClient();

	const {
		control,
		register,
		handleSubmit,
		watch,
		reset,
		formState: { errors, isSubmitting },
	} = useForm<SpilloverFormValues>({
		resolver: zodResolver(schema),
		defaultValues,
		mode: "onBlur",
	});

	const campaignId = watch("campaignId").trim();

	const query = useQuery<SpilloverView | null>({
		queryKey: ["spillover", campaignId],
		queryFn: ({ signal }) => getSpillover(campaignId, { signal }),
		enabled: campaignId.length > 0,
		staleTime: 15_000,
	});

	useEffect(() => {
		if (!query.data) return;
		reset({
			campaignId: query.data.campaign_id,
			mode: query.data.mode,
			capType: query.data.cap_type,
		});
	}, [query.data, reset]);

	const mutation = useMutation<SpilloverView, Error, SpilloverFormValues>({
		mutationFn: (values) =>
			saveSpillover({
				campaignId: values.campaignId.trim(),
				mode: values.mode,
				capType: values.capType,
			}),
		onSuccess: (data) => {
			toast.success("Spillover policy saved", {
				description: `Campaign ${data.campaign_id} now ${data.mode === "allow" ? "accepts over-cap" : "rejects over-cap"} ${data.cap_type}.`,
			});
			queryClient.setQueryData(["spillover", data.campaign_id], data);
		},
		onError: (err) => {
			const code =
				err instanceof SpilloverApiError ? err.code ?? err.message : err.message;
			toast.error("Could not save policy", { description: code });
		},
	});

	const onSubmit = handleSubmit((values) => mutation.mutate(values));
	const disabled = isSubmitting || mutation.isPending;
	const loadingPolicy = campaignId.length > 0 && query.isFetching;

	return (
		<form
			onSubmit={onSubmit}
			className="flex flex-col gap-5 rounded-lg border border-zinc-200 bg-white p-6 shadow-sm"
			aria-labelledby={`${formId}-title`}
		>
			<div>
				<label
					htmlFor={`${formId}-campaign`}
					className="block text-sm font-medium text-zinc-700"
				>
					Campaign ID
				</label>
				<input
					id={`${formId}-campaign`}
					type="text"
					autoComplete="off"
					spellCheck={false}
					className="mt-1 w-full rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-200"
					placeholder="cmp_..."
					{...register("campaignId")}
				/>
				{errors.campaignId && (
					<p className="mt-1 text-xs text-rose-600">
						{errors.campaignId.message}
					</p>
				)}
				{loadingPolicy && (
					<p className="mt-1 text-xs text-zinc-500">Loading current policy.</p>
				)}
			</div>

			<fieldset className="flex flex-col gap-2">
				<legend className="text-sm font-medium text-zinc-700">
					Over-cap behavior
				</legend>
				<Controller
					control={control}
					name="mode"
					render={({ field }) => (
						<div className="flex flex-col gap-2">
							<label className="flex cursor-pointer items-start gap-3 rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm hover:border-zinc-300">
								<input
									type="radio"
									name={field.name}
									value="cancel"
									checked={field.value === "cancel"}
									onChange={() => field.onChange("cancel")}
									className="mt-1 accent-emerald-600"
								/>
								<span className="flex flex-col">
									<span className="font-medium text-zinc-900">
										Cancel over-cap
									</span>
									<span className="text-xs text-zinc-500">
										Reject new conversions once the cap is reached.
									</span>
								</span>
							</label>
							<label className="flex cursor-pointer items-start gap-3 rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm hover:border-zinc-300">
								<input
									type="radio"
									name={field.name}
									value="allow"
									checked={field.value === "allow"}
									onChange={() => field.onChange("allow")}
									className="mt-1 accent-emerald-600"
								/>
								<span className="flex flex-col">
									<span className="font-medium text-zinc-900">
										Allow over-cap (spillover)
									</span>
									<span className="text-xs text-zinc-500">
										Keep accepting conversions beyond the cap.
									</span>
								</span>
							</label>
						</div>
					)}
				/>
				{errors.mode && (
					<p className="mt-1 text-xs text-rose-600">{errors.mode.message}</p>
				)}
			</fieldset>

			<div>
				<label
					htmlFor={`${formId}-captype`}
					className="block text-sm font-medium text-zinc-700"
				>
					Cap scope
				</label>
				<select
					id={`${formId}-captype`}
					className="mt-1 w-full rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-200"
					{...register("capType")}
				>
					<option value="conversions">Conversions</option>
					<option value="clicks">Clicks</option>
					<option value="any">Any cap</option>
				</select>
			</div>

			<div className="flex items-center justify-end gap-3">
				<button
					type="submit"
					disabled={disabled}
					className="inline-flex items-center justify-center rounded-md bg-zinc-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-60"
				>
					{disabled ? "Saving." : "Save policy"}
				</button>
			</div>
		</form>
	);
}
